import ENV from 'react-native-config';

import { AxiosResponse } from 'axios';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import {
    getRBTemplateviewSummary,
    getRbTemplateviewsHierarchy,
} from 'config/api';

import createMenuConfiguration from 'store/railsBuilder/utils/createMenuConfiguration';

import { RegularStorage, StorageKeys } from 'services/mmkv';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';
import isFunction from 'utils/function';

import {
    getMainMenuConfig,
    getMainMenuConfigFailure,
    getMainMenuConfigRequest,
    getMainMenuConfigSuccess,
    getTemplateview,
    getTemplateviewFailure,
    getTemplateviewRequest,
    getTemplateviewSuccess,
} from './actions';
import { GET_MAIN_MENU_CONFIG, GET_TEMPLATEVIEW } from './constants';
import fallbackTemplatesByIds from './fallback/contentdelivery/v1/templatesummary/index.ts';
import {
    MenuConfigurationItem,
    MenuItemType,
    RBHierarchyTemplateviewItem,
    RBTemplates,
    RBTemplateview,
} from './reducer.types';

export function* onGetMainMenuConfig({
    meta: { onSuccess, onFailure },
}: ReturnType<typeof getMainMenuConfig>) {
    try {
        yield put(getMainMenuConfigRequest());

        const hierarchyUrl = getRbTemplateviewsHierarchy();
        let hierarchyTemplates: RBHierarchyTemplateviewItem[] = [];

        try {
            const response: AxiosResponse<{
                totalRecords: number;
                templates: RBHierarchyTemplateviewItem[];
            }> = yield call(NagraInstance.get, hierarchyUrl);

            if (response?.data?.templates?.length) {
                hierarchyTemplates = response.data.templates;
            } else {
                throw new Error(
                    'No templates in the hierarchy response, falling back to mock data.',
                );
            }
        } catch (error) {
            const fallbackResponse = yield call(
                () =>
                    new Promise((resolve, reject) => {
                        import(
                            './fallback/contentdelivery/v1/templateviews/hierarchy.json'
                        )
                            .then(resolve)
                            .catch(reject);
                    }),
            );

            if (fallbackResponse?.templates?.length) {
                hierarchyTemplates = fallbackResponse.templates;
            }
        }

        let mobileTemplatesHierarchy =
            hierarchyTemplates.find(({ name }) => name === RBTemplates.Mobile)
                ?.children || [];
        const kidsTemplatesHierarchy =
            hierarchyTemplates.find(({ name }) => name === RBTemplates.Kids)
                ?.children || [];

        // TODO START Mock Recordings screen for PROD.
        if (ENV.ENV_NAME === 'PRODUCTION') {
            const recordingsTemplate = hierarchyTemplates
                // @ts-ignore
                .find(({ name }) => name === 'TEST')
                ?.children?.find(({ name }) => name === RBTemplates.Recordings);

            mobileTemplatesHierarchy = mobileTemplatesHierarchy.map((t) => {
                if (t.name === RBTemplates.Menu) {
                    const children = [...t.children];
                    // @ts-ignore
                    children.splice(2, 0, recordingsTemplate);

                    return {
                        ...t,
                        children,
                    };
                }

                return t;
            });
        }
        // TODO END Mock Recordings screen for PROD.

        const standardMenu: MenuConfigurationItem[] = yield call(
            createMenuConfiguration,
            mobileTemplatesHierarchy,
        );
        const kidsMenu: MenuConfigurationItem[] = yield call(
            createMenuConfiguration,
            kidsTemplatesHierarchy,
        );

        yield all(
            [...standardMenu, ...kidsMenu].map(function ({ id, type }) {
                if (
                    type === MenuItemType.MODULAR ||
                    type === MenuItemType.SEARCH
                ) {
                    return put(getTemplateview(id));
                }

                return undefined;
            }),
        );

        yield put(getMainMenuConfigSuccess(standardMenu, kidsMenu));

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error) {
        yield put(getMainMenuConfigFailure(error));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

export function* onGetTemplateview({
    payload: { id },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof getTemplateview>) {
    try {
        yield put(getTemplateviewRequest(id));

        const [templateviewResponse, templateviewPersisted]: [
            PromiseAllSettledConfig<AxiosResponse<RBTemplateview>>,
            PromiseAllSettledConfig<string>,
        ] = yield call(
            [Promise, 'allSettled'],
            [
                NagraInstance.get(getRBTemplateviewSummary(id)),
                RegularStorage.getItem(`${StorageKeys.TEMPLATEVIEW}_${id}`),
            ],
        );

        let data: RBTemplateview | undefined;

        if (
            templateviewResponse.status === 'fulfilled' &&
            templateviewResponse.value?.data
        ) {
            data = templateviewResponse.value.data;

            yield call(
                RegularStorage.setItem,
                `${StorageKeys.TEMPLATEVIEW}_${id}`,
                JSON.stringify(data),
            );
        } else if (
            templateviewResponse.status === 'rejected' &&
            templateviewPersisted.status === 'fulfilled' &&
            templateviewPersisted.value
        ) {
            const persistedTempleteview: RBTemplateview = JSON.parse(
                templateviewPersisted.value,
            );

            if (persistedTempleteview) {
                data = persistedTempleteview;
            }
        } else if (fallbackTemplatesByIds[id]) {
            data = fallbackTemplatesByIds[id];
        }

        if (!data) {
            throw new Error('No templateview data');
        }

        yield put(getTemplateviewSuccess(id, data));

        if (isFunction(onSuccess)) {
            yield call(onSuccess, data);
        }
    } catch (error: any) {
        yield put(getTemplateviewFailure(error, id));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

export default [
    takeLatest(GET_MAIN_MENU_CONFIG, onGetMainMenuConfig),
    takeEvery(GET_TEMPLATEVIEW, onGetTemplateview),
];
